import { FixaModal } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModal";
import { FixaPrompt } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaPrompt";
import { FixaModalHeader } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalHeader";
import { FixaModalFooter } from "@coworker/apprestructured/src/shared/wrappers/FixaModal/FixaModalFooter";
import { FixaText } from "@coworker/apprestructured/src/shared/wrappers/FixaText/FixaText";
import FixaButton from "@coworker/apprestructured/src/shared/wrappers/FixaButton/FixaButton";
import { useTranslation } from "@coworker/locales";
import { ReactNode } from "react";

interface AlertModalProps {
  visible: boolean;
  onClose: () => void;
  title: string;
  message: string | ReactNode;
  buttonText?: string;
}

/**
 * Generic alert modal component for displaying simple messages with a single action button.
 *
 * Use this component when you need to show:
 * - Error messages
 * - Informational alerts
 * - Simple confirmations with only one action
 *
 * @param visible - Controls modal visibility
 * @param onClose - Called when modal is closed (via close button, backdrop, or action button)
 * @param title - Main heading text displayed in the modal
 * @param message - Body text explaining the alert
 * @param buttonText - Optional custom text for the action button (defaults to "Ok, I understand")
 */
export const AlertModal = ({
  visible,
  onClose,
  title,
  message,
  buttonText,
}: AlertModalProps) => {
  const { t } = useTranslation();

  return (
    <FixaModal visible={visible} handleCloseBtn={onClose}>
      <FixaPrompt
        titleId="alert-modal"
        title=""
        header={
          <FixaModalHeader
            ariaCloseTxt={t("closeActionString")}
            closeBtnClick={onClose}
          />
        }
        footer={
          <FixaModalFooter>
            <FixaButton type="primary" onClick={onClose}>
              {buttonText ?? t("okIUnderstandString")}
            </FixaButton>
          </FixaModalFooter>
        }
      >
        <FixaText tagName="h2" textStyle="Heading.S">
          {title}
        </FixaText>
        <FixaText>{message}</FixaText>
      </FixaPrompt>
    </FixaModal>
  );
};
